export interface Language {
    code: string;
    name: string;
    nativeName: string;
    flag: string;
    dir: 'ltr' | 'rtl';
}

export const LANGUAGES: Language[] = [
    { 
        code: 'en', 
        name: 'English', 
        nativeName: 'English', 
        flag: '🇺🇸', 
        dir: 'ltr' 
    },
    { 
        code: 'ar', 
        name: 'Arabic', 
        nativeName: 'العربية', 
        flag: '🇸🇦', 
        dir: 'rtl' 
    },
    { 
        code: 'tr', 
        name: 'Turkish', 
        nativeName: 'Türkçe', 
        flag: '🇹🇷', 
        dir: 'ltr' 
    },
];

export const DEFAULT_LANGUAGE = 'en';

// Translation keys and texts
export const TRANSLATIONS = {
    en: {
        // Navigation
        'nav.home': 'Home',
        'nav.destinations': 'Destinations',
        'nav.hotels': 'Hotels',
        'nav.partnerHotels': 'Partner Hotels',
        'nav.flights': 'Flights',
        'nav.packages': 'Packages',
        'nav.about': 'About',
        'nav.signIn': 'Sign In',
        'nav.signUp': 'Sign Up',
        'nav.bookNow': 'Book Now',
        'nav.myAccount': 'My Account',
        'nav.signOut': 'Sign Out',
        'nav.hi': 'Hi',

        // Search Bar
        'search.city': 'City',
        'search.hotel': 'Hotel Name',
        'search.checkIn': 'Check In',
        'search.checkOut': 'Check Out',
        'search.search': 'Search',
        'search.cityPlaceholder': 'e.g., Dubai',
        'search.hotelPlaceholder': 'Optional',
        'search.errorCityOrHotel': 'Please enter city name or hotel name',
        'search.errorDates': 'Please select check-in and check-out dates',
        'search.errorDateOrder': 'Check-out date must be after check-in date',

        // Search Results
        'results.title': 'Search Results',
        'results.description': 'Find the best hotels for your stay.',
        'results.showingIn': 'Showing hotels in',
        'results.matching': 'matching',
        'results.loading': 'Loading hotels...',
        'results.noResults': 'No hotels found.',

        // Home Page
        'home.heroTitle': 'Discover Your Perfect Journey',
        'home.heroSubtitle': 'Explore the world with MB Tourism - Your trusted travel partner',
        'home.features': 'Why Choose Us',
        'home.featuresDesc': 'Experience the difference with our premium services',
        'home.destinations': 'Popular Destinations',
        'home.destinationsDesc': 'Discover the most sought-after travel destinations',
        'home.offers': 'Special Offers',
        'home.offersDesc': 'Limited time deals you don\'t want to miss',
        'home.cta': 'Ready to Start Your Journey?',
        'home.ctaDesc': 'Join thousands of travelers who trust MB Tourism for their adventures',
        'home.getStarted': 'Get Started',
        'home.contactUs': 'Contact Us',

        // AI Chat
        'ai.askMe': 'Ask Me',
        'ai.title': 'Smart Travel Assistant',
        'ai.status': 'Online Now',
        'ai.placeholder': 'Type your question here...',
        'ai.close': '✕',

        // Account
        'account.welcome': 'Welcome back',
        'account.description': 'Manage your account and view your travel history',
        'account.profileInfo': 'Profile Information',
        'account.editProfile': 'Edit Profile',
        'account.saveChanges': 'Save Changes',
        'account.cancelEdit': 'Cancel',
        'account.profileUpdated': 'Profile updated successfully!',
        'account.bookings': 'Bookings',
        'account.destinations': 'Destinations',
        'account.memberSince': 'Member Since',
        'account.status': 'Status',
        'account.recentActivity': 'Recent Activity',
        'account.accountSettings': 'Account Settings',
        'account.changePassword': 'Change Password',
        'account.privacySettings': 'Privacy Settings',
        'account.notifications': 'Notification Preferences',
        'account.logout': 'Sign Out',
        'account.fullName': 'Full Name',
        'account.email': 'Email',
        'account.userId': 'User ID',
        'account.enterFullName': 'Enter your full name',
        'account.enterEmail': 'Enter your email',
        'account.saving': 'Saving...',
        'account.confirmed': 'Confirmed',
        'account.completed': 'Completed',
        'account.viewed': 'Viewed',

        // Destinations
        'destinations.title': 'Destinations',
        'destinations.subtitle': 'Discover amazing places around the world',
        'destinations.featured': 'Featured Destinations',
        'destinations.featuredDesc': 'Our top picks for unforgettable experiences',
        'destinations.allDestinations': 'All Destinations',
        'destinations.destinationsFound': 'destinations found',
        'destinations.noDestinations': 'No destinations found matching your criteria',
        'destinations.noResultsTitle': 'No Destinations Found',
        'destinations.noResultsDesc': 'Try adjusting your filters or search criteria',
        'destinations.explore': 'Explore',
        'destinations.search': 'Search',
        'destinations.searchPlaceholder': 'Destination, country...',
        'destinations.continent': 'Continent',
        'destinations.allContinents': 'All Continents',
        'destinations.category': 'Category',
        'destinations.allCategories': 'All Categories',
        'destinations.filters': 'Filters',
        'destinations.clearFilters': 'Clear All',
        'destinations.priceRange': 'Price Range',
        'destinations.minRating': 'Minimum Rating',
        'destinations.sortBy': 'Sort By',
        'destinations.sortName': 'Name',
        'destinations.sortPrice': 'Price',
        'destinations.sortRating': 'Rating',
        'destinations.bestTime': 'Best Time',
        'destinations.duration': 'Duration',
        'destinations.price': 'Price',

        // Flights
        'flights.title': 'Flights',
        'flights.subtitle': 'Find the best flights to your destination',
        'flights.from': 'From',
        'flights.fromPlaceholder': 'City or Airport',
        'flights.to': 'To',
        'flights.toPlaceholder': 'City or Airport',
        'flights.departure': 'Departure',
        'flights.return': 'Return (Optional)',
        'flights.passengers': 'Passengers',
        'flights.cabinClass': 'Cabin Class',
        'flights.search': 'Search Flights',
        'flights.filters': 'Filters',
        'flights.clearFilters': 'Clear All',
        'flights.priceRange': 'Price Range',
        'flights.maxStops': 'Max Stops',
        'flights.direct': 'Direct',
        'flights.oneStop': '1 Stop',
        'flights.twoStops': '2 Stops',
        'flights.sortBy': 'Sort By',
        'flights.sortPrice': 'Price',
        'flights.sortDuration': 'Duration',
        'flights.sortDeparture': 'Departure Time',
        'flights.results': 'Results',
        'flights.flightsFound': 'flights found',
        'flights.noFlights': 'No flights found matching your criteria',
        'flights.noResultsTitle': 'No Flights Found',
        'flights.noResultsDesc': 'Try adjusting your search criteria',
        'flights.bookNow': 'Book Now',
        'flights.stops': 'stops',

        // Hotels
        'hotels.title': 'Hotels',
        'hotels.subtitle': 'Discover the perfect accommodation for your journey',
        'hotels.filters': 'Filters',
        'hotels.clearFilters': 'Clear All',
        'hotels.search': 'Search Hotels',
        'hotels.searchPlaceholder': 'Hotel name or location...',
        'hotels.city': 'City',
        'hotels.allCities': 'All Cities',
        'hotels.priceRange': 'Price Range',
        'hotels.minRating': 'Minimum Rating',
        'hotels.sortBy': 'Sort By',
        'hotels.sortName': 'Name',
        'hotels.sortPrice': 'Price',
        'hotels.sortRating': 'Rating',
        'hotels.results': 'Results',
        'hotels.hotelsFound': 'hotels found',
        'hotels.noHotels': 'No hotels found matching your criteria',
        'hotels.noResultsTitle': 'No Hotels Found',
        'hotels.noResultsDesc': 'Try adjusting your filters or search criteria',
        'hotels.bookNow': 'Book Now',

        // Packages
        'packages.title': 'Travel Packages',
        'packages.subtitle': 'Discover amazing travel packages tailored for every budget and preference',
        'packages.featured': 'Featured Packages',
        'packages.featuredDesc': 'Our most popular and highly-rated travel experiences',
        'packages.allPackages': 'All Packages',
        'packages.packagesFound': 'packages found',
        'packages.noPackages': 'No packages found matching your criteria',
        'packages.noResultsTitle': 'No Packages Found',
        'packages.noResultsDesc': 'Try adjusting your filters or search criteria',
        'packages.explore': 'Explore Package',
        'packages.search': 'Search',
        'packages.searchPlaceholder': 'Package, destination...',
        'packages.category': 'Category',
        'packages.allCategories': 'All Categories',
        'packages.duration': 'Duration',
        'packages.allDurations': 'All Durations',
        'packages.priceRange': 'Price Range',
        'packages.minRating': 'Minimum Rating',
        'packages.sortBy': 'Sort By',
        'packages.sortName': 'Name',
        'packages.sortPrice': 'Price',
        'packages.sortRating': 'Rating',
        'packages.sortDuration': 'Duration',
        'packages.groupSize': 'Group Size',
        'packages.difficulty': 'Difficulty',
        'packages.price': 'Price',
        'packages.filters': 'Filters',
        'packages.clearFilters': 'Clear All',

        // About Us
        'about.hero.title': 'About MB Tourism',
        'about.hero.subtitle': 'Your trusted partner in creating unforgettable travel experiences since 2009',
        'about.story.title': 'Our Story',
        'about.story.paragraph1': 'MB Tourism was founded with a simple yet powerful vision: to make world-class travel accessible to everyone. What started as a small local agency has grown into a comprehensive travel platform serving thousands of satisfied customers.',
        'about.story.paragraph2': 'Over the years, we have built strong partnerships with leading hotels, airlines, and tour operators worldwide, ensuring our customers receive the best rates and exceptional service.',
        'about.story.paragraph3': 'Our commitment to excellence and customer satisfaction has earned us the trust of travelers from all walks of life, making us one of the most reliable names in the tourism industry.',
        'about.stats.clients': 'Happy Clients',
        'about.stats.destinations': 'Destinations',
        'about.stats.years': 'Years Experience',
        'about.stats.satisfaction': 'Satisfaction Rate',
        'about.values.title': 'Our Core Values',
        'about.values.subtitle': 'The principles that guide everything we do',
        'about.values.quality.title': 'Quality Service',
        'about.values.quality.desc': 'We never compromise on the quality of our services, ensuring every trip meets the highest standards.',
        'about.values.trust.title': 'Trust & Reliability',
        'about.values.trust.desc': 'Building lasting relationships through transparency, honesty, and consistent delivery of promises.',
        'about.values.innovation.title': 'Innovation',
        'about.values.innovation.desc': 'Continuously improving our platform and services to provide cutting-edge travel solutions.',
        'about.values.sustainability.title': 'Sustainability',
        'about.values.sustainability.desc': 'Promoting responsible tourism that benefits local communities and preserves our planet.',
        'about.team.title': 'Meet Our Team',
        'about.team.subtitle': 'The passionate professionals behind MB Tourism\'s success',
        'about.team.ceo': 'Chief Executive Officer',
        'about.team.ceo_desc': 'Leading our company with over 20 years of experience in the tourism industry.',
        'about.team.cto': 'Chief Technology Officer',
        'about.team.cto_desc': 'Driving innovation and digital transformation across our platform.',
        'about.team.cfo': 'Chief Financial Officer',
        'about.team.cfo_desc': 'Ensuring financial stability and strategic growth for sustainable success.',
        'about.team.cmo': 'Chief Marketing Officer',
        'about.team.cmo_desc': 'Building our brand and connecting with travelers worldwide.',
        'about.mission.title': 'Our Mission',
        'about.mission.description': 'To inspire and enable people to explore the world by providing exceptional travel experiences, innovative technology, and personalized service that exceeds expectations.',
        'about.cta.title': 'Ready to Start Your Journey?',
        'about.cta.description': 'Join thousands of satisfied travelers who trust MB Tourism for their adventures. Let us help you create memories that will last a lifetime.',
        'about.cta.button': 'Start Planning Now',

        // Auth
        'auth.welcomeBack': 'Welcome back',
        'auth.signInToContinue': 'Sign in to continue to MB Tourism',
        'auth.email': 'Email',
        'auth.emailPlaceholder': 'you@example.com',
        'auth.password': 'Password',
        'auth.passwordPlaceholder': '••••••••',
        'auth.signIn': 'Sign In',
        'auth.signingIn': 'Signing in...',
        'auth.noAccount': 'Don\'t have an account?',
        'auth.createAccount': 'Create one',
        'auth.nameRequired': 'Name is required',
        'auth.emailRequired': 'Email is required',
        'auth.passwordRequired': 'Password is required',
        'auth.invalidEmail': 'Invalid email',
        'auth.passwordMinLength': 'Password must be at least 4 characters',
        'auth.loginFailed': 'Login failed',
        'auth.fullName': 'Full name',
        'auth.namePlaceholder': 'Your Name',
        'auth.joinInMinute': 'Join MB Tourism in a minute',
        'auth.registrationFailed': 'Registration failed',
        'auth.creatingAccount': 'Creating account...',
        'auth.haveAccount': 'Already have an account?',

        // Forgot Password
        'forgotPassword.title': 'Forgot Password?',
        'forgotPassword.subtitle': 'Enter your email address and we\'ll send you a link to reset your password.',
        'forgotPassword.email': 'Email Address',
        'forgotPassword.emailPlaceholder': 'Enter your email address',
        'forgotPassword.sendResetLink': 'Send Reset Link',
        'forgotPassword.sending': 'Sending...',
        'forgotPassword.successMessage': 'Password reset link has been sent to your email address.',
        'forgotPassword.errorMessage': 'Please enter a valid email address.',
        'forgotPassword.rememberPassword': 'Remember your password?',
        'forgotPassword.backToLogin': 'Back to Login',
        'forgotPassword.noAccount': 'Don\'t have an account?',
        'forgotPassword.createAccount': 'Create Account',

        // Reset Password
        'resetPassword.title': 'Reset Password',
        'resetPassword.subtitle': 'Enter your new password below.',
        'resetPassword.newPassword': 'New Password',
        'resetPassword.passwordPlaceholder': 'Enter your new password',
        'resetPassword.confirmPassword': 'Confirm Password',
        'resetPassword.confirmPasswordPlaceholder': 'Confirm your new password',
        'resetPassword.requirements': 'Password Requirements',
        'resetPassword.minLength': 'At least 6 characters',
        'resetPassword.passwordsMatch': 'Passwords match',
        'resetPassword.resetPassword': 'Reset Password',
        'resetPassword.resetting': 'Resetting...',
        'resetPassword.successMessage': 'Password has been reset successfully! Redirecting to login...',
        'resetPassword.passwordsNotMatch': 'Passwords do not match.',
        'resetPassword.passwordTooShort': 'Password must be at least 6 characters long.',
        'resetPassword.invalidToken': 'Invalid or expired reset link.',
        'resetPassword.invalidLink': 'Invalid Reset Link',
        'resetPassword.invalidLinkMessage': 'This password reset link is invalid or has expired.',
        'resetPassword.requestNewLink': 'Request New Link',
        'resetPassword.rememberPassword': 'Remember your password?',
        'resetPassword.backToLogin': 'Back to Login',

        // Partner Hotels
        'partnerHotels.title': 'Partner Hotels',
        'partnerHotels.subtitle': 'Discover our exclusive collection of luxury hotels and resorts worldwide',
        'partnerHotels.filters': 'Filters',
        'partnerHotels.clearFilters': 'Clear All',
        'partnerHotels.search': 'Search',
        'partnerHotels.searchPlaceholder': 'Hotel name or location...',
        'partnerHotels.location': 'Location',
        'partnerHotels.allLocations': 'All Locations',
        'partnerHotels.stars': 'Stars',
        'partnerHotels.category': 'Category',
        'partnerHotels.allCategories': 'All Categories',
        'partnerHotels.categories.luxury': 'Luxury',
        'partnerHotels.categories.business': 'Business',
        'partnerHotels.categories.resort': 'Resort',
        'partnerHotels.categories.boutique': 'Boutique',
        'partnerHotels.categories.apartment': 'Apartment',
        'partnerHotels.sortBy': 'Sort By',
        'partnerHotels.sortName': 'Name',
        'partnerHotels.sortPrice': 'Price',
        'partnerHotels.sortStars': 'Stars',
        'partnerHotels.results': 'Results',
        'partnerHotels.hotelsFound': 'hotels found',
        'partnerHotels.noResultsTitle': 'No Hotels Found',
        'partnerHotels.noResultsDesc': 'Try adjusting your filters or search criteria',
        'partnerHotels.exclusive': 'Exclusive',
        'partnerHotels.from': 'From',
        'partnerHotels.discount': 'Discount',
        'partnerHotels.viewDetails': 'View Details',

        // Common
        'common.loading': 'Loading...',
        'common.error': 'Error',
        'common.success': 'Success',
        'common.save': 'Save',
        'common.cancel': 'Cancel',
        'common.edit': 'Edit',
        'common.delete': 'Delete',

        // Hotel Details
        'hotelDetails.loading': 'Loading hotel details...',
        'hotelDetails.notFound': 'Hotel not found',
        'hotelDetails.notFoundDesc': 'The hotel you\'re looking for doesn\'t exist.',
        'hotelDetails.backToHotels': 'Back to Hotels',
        'hotelDetails.overview': 'Overview',
        'hotelDetails.rooms': 'Rooms & Rates',
        'hotelDetails.amenities': 'Amenities',
        'hotelDetails.reviews': 'Reviews',
        'hotelDetails.aboutHotel': 'About this hotel',
        'hotelDetails.whatMakesSpecial': 'What makes this hotel special',
        'hotelDetails.hotelPolicies': 'Hotel Policies',
        'hotelDetails.availableRooms': 'Available Rooms',
        'hotelDetails.hotelAmenities': 'Hotel Amenities',
        'hotelDetails.guestReviews': 'Guest Reviews',
        'hotelDetails.startingFrom': 'Starting from',
        'hotelDetails.perNight': 'per night',
        'hotelDetails.checkIn': 'Check-in',
        'hotelDetails.checkOut': 'Check-out',
        'hotelDetails.guests': 'Guests',
        'hotelDetails.checkAvailability': 'Check Availability',
        'hotelDetails.contactHotel': 'Contact Hotel',
        'hotelDetails.selectRoom': 'Select Room',
        'hotelDetails.viewDetails': 'View Details',
        'hotelDetails.wasHelpful': 'Was this helpful?',
        'hotelDetails.amenityCategories': {
            'general': 'General',
            'room': 'Room Amenities',
            'dining': 'Dining',
            'wellness': 'Wellness & Recreation',
            'business': 'Business'
        },
        'hotelDetails.highlight1': 'Prime location in historic Sultanahmet',
        'hotelDetails.highlight2': 'Stunning views of Blue Mosque and Hagia Sophia',
        'hotelDetails.highlight3': 'Luxury spa and wellness center',
        'hotelDetails.highlight4': 'Multiple dining options with Turkish cuisine',
        'hotelDetails.highlight5': '24/7 concierge service',
        'hotelDetails.cancellation': 'Cancellation',
        'hotelDetails.pets': 'Pets',
        'hotelDetails.smoking': 'Smoking',
        
        // Booking translations
        'booking.title': 'Book Your Stay',
        'booking.newSearch': 'New Search',
        'booking.selectDates': 'Please select check-in and check-out dates',
        'booking.invalidDates': 'Check-out date must be after check-in date',
        'booking.searchError': 'Error searching for availability',
        'booking.guest': 'Guest',
        'booking.guests': 'Guests',
        'booking.searching': 'Searching...',
        'booking.selectRoom': 'Select Your Room',
        'booking.roomType1': 'Deluxe Room',
        'booking.roomType2': 'Executive Suite',
        'booking.roomType3': 'Presidential Suite',
        'booking.roomDescription1': 'Comfortable room with city view',
        'booking.roomDescription2': 'Spacious suite with separate living area',
        'booking.roomDescription3': 'Ultimate luxury with private terrace',
        'booking.roomsAvailable': 'rooms available',
        'booking.soldOut': 'Sold Out',
        'booking.bookingSummary': 'Booking Summary',
        'booking.dates': 'Dates',
        'booking.total': 'Total',
        'booking.continue': 'Continue',
        'booking.guestDetails': 'Guest Details',
        'booking.firstName': 'First Name',
        'booking.lastName': 'Last Name',
        'booking.email': 'Email',
        'booking.phone': 'Phone',
        'booking.specialRequests': 'Special Requests',
        'booking.specialRequestsPlaceholder': 'Any special requests or preferences...',
        'booking.fillRequiredFields': 'Please fill in all required fields',
        'booking.bookingError': 'Error creating booking',
        'booking.processing': 'Processing...',
        'booking.confirmBooking': 'Confirm Booking',
        'booking.bookingConfirmed': 'Booking Confirmed!',
        'booking.confirmationMessage': 'Your booking has been successfully confirmed. You will receive a confirmation email shortly.',
        'booking.confirmationNumber': 'Confirmation Number',
        'booking.bookingId': 'Booking ID',
        'booking.totalPaid': 'Total Paid',
        'booking.printConfirmation': 'Print Confirmation',
        'booking.newBooking': 'New Booking',
    },

    ar: {
        // Navigation
        'nav.home': 'الرئيسية',
        'nav.destinations': 'الوجهات',
        'nav.hotels': 'الفنادق',
        'nav.partnerHotels': 'الفنادق الشريكة',
        'nav.flights': 'الطيران',
        'nav.packages': 'الباقات',
        'nav.about': 'حولنا',
        'nav.signIn': 'تسجيل الدخول',
        'nav.signUp': 'إنشاء حساب',
        'nav.bookNow': 'احجز الآن',
        'nav.myAccount': 'حسابي',
        'nav.signOut': 'تسجيل الخروج',
        'nav.hi': 'مرحباً',

        // Search Bar
        'search.city': 'المدينة',
        'search.hotel': 'اسم الفندق',
        'search.checkIn': 'تاريخ الدخول',
        'search.checkOut': 'تاريخ الخروج',
        'search.search': 'بحث',
        'search.cityPlaceholder': 'مثال: دبي',
        'search.hotelPlaceholder': 'اختياري',
        'search.errorCityOrHotel': 'الرجاء إدخال اسم المدينة أو اسم الفندق',
        'search.errorDates': 'الرجاء اختيار تاريخ الدخول والخروج',
        'search.errorDateOrder': 'تاريخ الخروج يجب أن يكون بعد تاريخ الدخول',

        // Search Results
        'results.title': 'نتائج البحث',
        'results.description': 'اعثر على أفضل الفنادق لإقامتك.',
        'results.showingIn': 'عرض الفنادق في',
        'results.matching': 'التي تطابق',
        'results.loading': 'جارٍ تحميل الفنادق...',
        'results.noResults': 'لم يتم العثور على فنادق.',

        // Home Page
        'home.heroTitle': 'اكتشف رحلتك المثالية',
        'home.heroSubtitle': 'استكشف العالم مع MB Tourism - شريكك الموثوق في السفر',
        'home.features': 'لماذا تختارنا',
        'home.featuresDesc': 'اختبر الفرق مع خدماتنا المميزة',
        'home.destinations': 'الوجهات الشائعة',
        'home.destinationsDesc': 'اكتشف أكثر الوجهات السياحية طلباً',
        'home.offers': 'العروض الخاصة',
        'home.offersDesc': 'عروض محدودة الوقت لا تريد تفويتها',
        'home.cta': 'هل أنت مستعد لبدء رحلتك؟',
        'home.ctaDesc': 'انضم إلى آلاف المسافرين الذين يثقون في MB Tourism لمغامراتهم',
        'home.getStarted': 'ابدأ الآن',
        'home.contactUs': 'اتصل بنا',

        // AI Chat
        'ai.askMe': 'اسألني',
        'ai.title': 'المساعد السياحي الذكي',
        'ai.status': 'متصل الآن',
        'ai.placeholder': 'اكتب سؤالك هنا...',
        'ai.close': '✕',

        // Account
        'account.welcome': 'مرحباً بك مرة أخرى',
        'account.description': 'إدارة حسابك وعرض تاريخ السفر',
        'account.profileInfo': 'معلومات الملف الشخصي',
        'account.editProfile': 'تعديل الملف الشخصي',
        'account.saveChanges': 'حفظ التغييرات',
        'account.cancelEdit': 'إلغاء',
        'account.profileUpdated': 'تم تحديث الملف الشخصي بنجاح!',
        'account.bookings': 'الحجوزات',
        'account.destinations': 'الوجهات',
        'account.memberSince': 'عضو منذ',
        'account.status': 'الحالة',
        'account.recentActivity': 'النشاط الأخير',
        'account.accountSettings': 'إعدادات الحساب',
        'account.changePassword': 'تغيير كلمة المرور',
        'account.privacySettings': 'إعدادات الخصوصية',
        'account.notifications': 'تفضيلات الإشعارات',
        'account.logout': 'تسجيل الخروج',
        'account.fullName': 'الاسم الكامل',
        'account.email': 'البريد الإلكتروني',
        'account.userId': 'معرف المستخدم',
        'account.enterFullName': 'أدخل اسمك الكامل',
        'account.enterEmail': 'أدخل بريدك الإلكتروني',
        'account.saving': 'جاري الحفظ...',
        'account.confirmed': 'مؤكد',
        'account.completed': 'مكتمل',
        'account.viewed': 'تم العرض',

        // Auth
        'auth.welcomeBack': 'مرحباً بك مرة أخرى',
        'auth.signInToContinue': 'سجل الدخول للمتابعة إلى MB Tourism',
        'auth.email': 'البريد الإلكتروني',
        'auth.emailPlaceholder': 'you@example.com',
        'auth.password': 'كلمة المرور',
        'auth.passwordPlaceholder': '••••••••',
        'auth.signIn': 'تسجيل الدخول',
        'auth.signingIn': 'جارٍ تسجيل الدخول...',
        'auth.noAccount': 'ليس لديك حساب؟',
        'auth.createAccount': 'إنشاء حساب',
        'auth.nameRequired': 'الاسم مطلوب',
        'auth.emailRequired': 'البريد الإلكتروني مطلوب',
        'auth.passwordRequired': 'كلمة المرور مطلوبة',
        'auth.invalidEmail': 'بريد إلكتروني غير صحيح',
        'auth.passwordMinLength': 'كلمة المرور يجب أن تكون 4 أحرف على الأقل',
        'auth.loginFailed': 'فشل في تسجيل الدخول',
        'auth.fullName': 'الاسم الكامل',
        'auth.namePlaceholder': 'اسمك',
        'auth.joinInMinute': 'انضم إلى MB Tourism في دقيقة',
        'auth.registrationFailed': 'فشل في التسجيل',
        'auth.creatingAccount': 'جارٍ إنشاء الحساب...',
        'auth.haveAccount': 'لديك حساب بالفعل؟',

        // Forgot Password
        'forgotPassword.title': 'نسيت كلمة المرور؟',
        'forgotPassword.subtitle': 'أدخل عنوان بريدك الإلكتروني وسنرسل لك رابطاً لإعادة تعيين كلمة المرور.',
        'forgotPassword.email': 'عنوان البريد الإلكتروني',
        'forgotPassword.emailPlaceholder': 'أدخل عنوان بريدك الإلكتروني',
        'forgotPassword.sendResetLink': 'إرسال رابط الإعادة',
        'forgotPassword.sending': 'جارٍ الإرسال...',
        'forgotPassword.successMessage': 'تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني.',
        'forgotPassword.errorMessage': 'يرجى إدخال عنوان بريد إلكتروني صحيح.',
        'forgotPassword.rememberPassword': 'تذكرت كلمة المرور؟',
        'forgotPassword.backToLogin': 'العودة لتسجيل الدخول',
        'forgotPassword.noAccount': 'ليس لديك حساب؟',
        'forgotPassword.createAccount': 'إنشاء حساب',

        // Reset Password
        'resetPassword.title': 'إعادة تعيين كلمة المرور',
        'resetPassword.subtitle': 'أدخل كلمة المرور الجديدة أدناه.',
        'resetPassword.newPassword': 'كلمة المرور الجديدة',
        'resetPassword.passwordPlaceholder': 'أدخل كلمة المرور الجديدة',
        'resetPassword.confirmPassword': 'تأكيد كلمة المرور',
        'resetPassword.confirmPasswordPlaceholder': 'أكد كلمة المرور الجديدة',
        'resetPassword.requirements': 'متطلبات كلمة المرور',
        'resetPassword.minLength': '6 أحرف على الأقل',
        'resetPassword.passwordsMatch': 'كلمات المرور متطابقة',
        'resetPassword.resetPassword': 'إعادة تعيين كلمة المرور',
        'resetPassword.resetting': 'جارٍ إعادة التعيين...',
        'resetPassword.successMessage': 'تم إعادة تعيين كلمة المرور بنجاح! جارٍ التوجيه لتسجيل الدخول...',
        'resetPassword.passwordsNotMatch': 'كلمات المرور غير متطابقة.',
        'resetPassword.passwordTooShort': 'كلمة المرور يجب أن تكون 6 أحرف على الأقل.',
        'resetPassword.invalidToken': 'رابط إعادة التعيين غير صحيح أو منتهي الصلاحية.',
        'resetPassword.invalidLink': 'رابط إعادة تعيين غير صحيح',
        'resetPassword.invalidLinkMessage': 'رابط إعادة تعيين كلمة المرور هذا غير صحيح أو منتهي الصلاحية.',
        'resetPassword.requestNewLink': 'طلب رابط جديد',
        'resetPassword.rememberPassword': 'تذكرت كلمة المرور؟',
        'resetPassword.backToLogin': 'العودة لتسجيل الدخول',

        // Destinations
        'destinations.title': 'الوجهات',
        'destinations.subtitle': 'اكتشف أماكن مذهلة حول العالم',
        'destinations.featured': 'الوجهات المميزة',
        'destinations.featuredDesc': 'اختياراتنا الأولى لتجارب لا تُنسى',
        'destinations.allDestinations': 'جميع الوجهات',
        'destinations.destinationsFound': 'وجهة تم العثور عليها',
        'destinations.noDestinations': 'لم يتم العثور على وجهات تطابق معاييرك',
        'destinations.noResultsTitle': 'لم يتم العثور على وجهات',
        'destinations.noResultsDesc': 'حاول تعديل المرشحات أو معايير البحث',
        'destinations.explore': 'استكشف',
        'destinations.search': 'البحث',
        'destinations.searchPlaceholder': 'الوجهة، البلد...',
        'destinations.continent': 'القارة',
        'destinations.allContinents': 'جميع القارات',
        'destinations.category': 'الفئة',
        'destinations.allCategories': 'جميع الفئات',
        'destinations.filters': 'المرشحات',
        'destinations.clearFilters': 'مسح الكل',
        'destinations.priceRange': 'نطاق السعر',
        'destinations.minRating': 'التقييم الأدنى',
        'destinations.sortBy': 'ترتيب حسب',
        'destinations.sortName': 'الاسم',
        'destinations.sortPrice': 'السعر',
        'destinations.sortRating': 'التقييم',
        'destinations.bestTime': 'أفضل وقت',
        'destinations.duration': 'المدة',
        'destinations.price': 'السعر',

        // Flights
        'flights.title': 'الطيران',
        'flights.subtitle': 'اعثر على أفضل الرحلات إلى وجهتك',
        'flights.from': 'من',
        'flights.fromPlaceholder': 'المدينة أو المطار',
        'flights.to': 'إلى',
        'flights.toPlaceholder': 'المدينة أو المطار',
        'flights.departure': 'تاريخ المغادرة',
        'flights.return': 'تاريخ العودة (اختياري)',
        'flights.passengers': 'عدد المسافرين',
        'flights.cabinClass': 'فئة المقصورة',
        'flights.search': 'البحث في الرحلات',
        'flights.filters': 'المرشحات',
        'flights.clearFilters': 'مسح الكل',
        'flights.priceRange': 'نطاق السعر',
        'flights.maxStops': 'الحد الأقصى للتوقف',
        'flights.direct': 'مباشر',
        'flights.oneStop': 'توقف واحد',
        'flights.twoStops': 'توقفان',
        'flights.sortBy': 'ترتيب حسب',
        'flights.sortPrice': 'السعر',
        'flights.sortDuration': 'المدة',
        'flights.sortDeparture': 'وقت المغادرة',
        'flights.results': 'النتائج',
        'flights.flightsFound': 'رحلة تم العثور عليها',
        'flights.noFlights': 'لم يتم العثور على رحلات تطابق معاييرك',
        'flights.noResultsTitle': 'لم يتم العثور على رحلات',
        'flights.noResultsDesc': 'حاول تعديل معايير البحث',
        'flights.bookNow': 'احجز الآن',
        'flights.stops': 'توقف',

        // Hotels
        'hotels.title': 'الفنادق',
        'hotels.subtitle': 'اكتشف الإقامة المثالية لرحلتك',
        'hotels.filters': 'المرشحات',
        'hotels.clearFilters': 'مسح الكل',
        'hotels.search': 'البحث في الفنادق',
        'hotels.searchPlaceholder': 'اسم الفندق أو الموقع...',
        'hotels.city': 'المدينة',
        'hotels.allCities': 'جميع المدن',
        'hotels.priceRange': 'نطاق السعر',
        'hotels.minRating': 'التقييم الأدنى',
        'hotels.sortBy': 'ترتيب حسب',
        'hotels.sortName': 'الاسم',
        'hotels.sortPrice': 'السعر',
        'hotels.sortRating': 'التقييم',
        'hotels.results': 'النتائج',
        'hotels.hotelsFound': 'فندق تم العثور عليه',
        'hotels.noHotels': 'لم يتم العثور على فنادق تطابق معاييرك',
        'hotels.noResultsTitle': 'لم يتم العثور على فنادق',
        'hotels.noResultsDesc': 'حاول تعديل المرشحات أو معايير البحث',
        'hotels.bookNow': 'احجز الآن',

        // Packages
        'packages.title': 'الباقات السياحية',
        'packages.subtitle': 'اكتشف باقات سفر مذهلة مصممة لكل ميزانية وتفضيل',
        'packages.featured': 'الباقات المميزة',
        'packages.featuredDesc': 'تجارب السفر الأكثر شعبية وتقييماً عالياً',
        'packages.allPackages': 'جميع الباقات',
        'packages.packagesFound': 'باقة تم العثور عليها',
        'packages.noPackages': 'لم يتم العثور على باقات تطابق معاييرك',
        'packages.noResultsTitle': 'لم يتم العثور على باقات',
        'packages.noResultsDesc': 'حاول تعديل المرشحات أو معايير البحث',
        'packages.explore': 'استكشف الباقة',
        'packages.search': 'البحث',
        'packages.searchPlaceholder': 'الباقة، الوجهة...',
        'packages.category': 'الفئة',
        'packages.allCategories': 'جميع الفئات',
        'packages.duration': 'المدة',
        'packages.allDurations': 'جميع المدد',
        'packages.priceRange': 'نطاق السعر',
        'packages.minRating': 'التقييم الأدنى',
        'packages.sortBy': 'ترتيب حسب',
        'packages.sortName': 'الاسم',
        'packages.sortPrice': 'السعر',
        'packages.sortRating': 'التقييم',
        'packages.sortDuration': 'المدة',
        'packages.groupSize': 'حجم المجموعة',
        'packages.difficulty': 'مستوى الصعوبة',
        'packages.price': 'السعر',
        'packages.filters': 'المرشحات',
        'packages.clearFilters': 'مسح الكل',

        // About Us
        'about.hero.title': 'حول MB Tourism',
        'about.hero.subtitle': 'شريكك الموثوق في إنشاء تجارب سفر لا تُنسى منذ عام 2009',
        'about.story.title': 'قصتنا',
        'about.story.paragraph1': 'تأسست MB Tourism برؤية بسيطة لكنها قوية: جعل السفر من الدرجة الأولى متاحاً للجميع. ما بدأ كوكالة محلية صغيرة نما ليصبح منصة سفر شاملة تخدم آلاف العملاء الراضين.',
        'about.story.paragraph2': 'على مر السنين، بنينا شراكات قوية مع الفنادق والخطوط الجوية ومشغلي الرحلات الرائدين في العالم، مما يضمن لعملائنا أفضل الأسعار وخدمة استثنائية.',
        'about.story.paragraph3': 'التزامنا بالتميز ورضا العملاء أكسبنا ثقة المسافرين من جميع مناحي الحياة، مما جعلنا من أكثر الأسماء موثوقية في صناعة السياحة.',
        'about.stats.clients': 'عميل سعيد',
        'about.stats.destinations': 'وجهة',
        'about.stats.years': 'سنوات خبرة',
        'about.stats.satisfaction': 'معدل الرضا',
        'about.values.title': 'قيمنا الأساسية',
        'about.values.subtitle': 'المبادئ التي توجه كل ما نقوم به',
        'about.values.quality.title': 'خدمة عالية الجودة',
        'about.values.quality.desc': 'نحن لا نتنازل أبداً عن جودة خدماتنا، مما يضمن أن كل رحلة تلبي أعلى المعايير.',
        'about.values.trust.title': 'الثقة والموثوقية',
        'about.values.trust.desc': 'بناء علاقات دائمة من خلال الشفافية والصدق والتسليم المتسق للوعود.',
        'about.values.innovation.title': 'الابتكار',
        'about.values.innovation.desc': 'التحسين المستمر لمنصتنا وخدماتنا لتقديم حلول سفر متطورة.',
        'about.values.sustainability.title': 'الاستدامة',
        'about.values.sustainability.desc': 'تعزيز السياحة المسؤولة التي تفيد المجتمعات المحلية وتحافظ على كوكبنا.',
        'about.team.title': 'تعرف على فريقنا',
        'about.team.subtitle': 'المهنيون المتحمسون وراء نجاح MB Tourism',
        'about.team.ceo': 'الرئيس التنفيذي',
        'about.team.ceo_desc': 'يقود شركتنا بأكثر من 20 عاماً من الخبرة في صناعة السياحة.',
        'about.team.cto': 'الرئيس التنفيذي للتكنولوجيا',
        'about.team.cto_desc': 'قيادة الابتكار والتحول الرقمي عبر منصتنا.',
        'about.team.cfo': 'الرئيس التنفيذي للمالية',
        'about.team.cfo_desc': 'ضمان الاستقرار المالي والنمو الاستراتيجي للنجاح المستدام.',
        'about.team.cmo': 'الرئيس التنفيذي للتسويق',
        'about.team.cmo_desc': 'بناء علامتنا التجارية والتواصل مع المسافرين حول العالم.',
        'about.mission.title': 'مهمتنا',
        'about.mission.description': 'إلهام وتمكين الناس لاستكشاف العالم من خلال تقديم تجارب سفر استثنائية وتكنولوجيا مبتكرة وخدمة مخصصة تتجاوز التوقعات.',
        'about.cta.title': 'هل أنت مستعد لبدء رحلتك؟',
        'about.cta.description': 'انضم إلى آلاف المسافرين الراضين الذين يثقون في MB Tourism لمغامراتهم. دعنا نساعدك في إنشاء ذكريات تدوم مدى الحياة.',
        'about.cta.button': 'ابدأ التخطيط الآن',

        // Partner Hotels
        'partnerHotels.title': 'الفنادق الشريكة',
        'partnerHotels.subtitle': 'اكتشف مجموعتنا الحصرية من الفنادق الفاخرة والمنتجعات حول العالم',
        'partnerHotels.filters': 'المرشحات',
        'partnerHotels.clearFilters': 'مسح الكل',
        'partnerHotels.search': 'البحث',
        'partnerHotels.searchPlaceholder': 'اسم الفندق أو الموقع...',
        'partnerHotels.location': 'الموقع',
        'partnerHotels.allLocations': 'جميع المواقع',
        'partnerHotels.stars': 'النجوم',
        'partnerHotels.category': 'الفئة',
        'partnerHotels.allCategories': 'جميع الفئات',
        'partnerHotels.categories.luxury': 'فاخر',
        'partnerHotels.categories.business': 'أعمال',
        'partnerHotels.categories.resort': 'منتجع',
        'partnerHotels.categories.boutique': 'بوتيك',
        'partnerHotels.categories.apartment': 'شقة',
        'partnerHotels.sortBy': 'ترتيب حسب',
        'partnerHotels.sortName': 'الاسم',
        'partnerHotels.sortPrice': 'السعر',
        'partnerHotels.sortStars': 'النجوم',
        'partnerHotels.results': 'النتائج',
        'partnerHotels.hotelsFound': 'فندق تم العثور عليه',
        'partnerHotels.noResultsTitle': 'لم يتم العثور على فنادق',
        'partnerHotels.noResultsDesc': 'حاول تعديل المرشحات أو معايير البحث',
        'partnerHotels.exclusive': 'حصري',
        'partnerHotels.from': 'من',
        'partnerHotels.discount': 'خصم',
        'partnerHotels.viewDetails': 'عرض التفاصيل',

        // Common
        'common.loading': 'جارٍ التحميل...',
        'common.error': 'خطأ',
        'common.success': 'نجح',
        'common.save': 'حفظ',
        'common.cancel': 'إلغاء',
        'common.edit': 'تعديل',
        'common.delete': 'حذف',

        // Hotel Details
        'hotelDetails.loading': 'جارٍ تحميل تفاصيل الفندق...',
        'hotelDetails.notFound': 'الفندق غير موجود',
        'hotelDetails.notFoundDesc': 'الفندق الذي تبحث عنه غير موجود.',
        'hotelDetails.backToHotels': 'العودة للفنادق',
        'hotelDetails.overview': 'نظرة عامة',
        'hotelDetails.rooms': 'الغرف والأسعار',
        'hotelDetails.amenities': 'المرافق',
        'hotelDetails.reviews': 'التقييمات',
        'hotelDetails.aboutHotel': 'حول هذا الفندق',
        'hotelDetails.whatMakesSpecial': 'ما يجعل هذا الفندق مميزاً',
        'hotelDetails.hotelPolicies': 'سياسات الفندق',
        'hotelDetails.availableRooms': 'الغرف المتاحة',
        'hotelDetails.hotelAmenities': 'مرافق الفندق',
        'hotelDetails.guestReviews': 'تقييمات الضيوف',
        'hotelDetails.startingFrom': 'يبدأ من',
        'hotelDetails.perNight': 'في الليلة',
        'hotelDetails.checkIn': 'تاريخ الدخول',
        'hotelDetails.checkOut': 'تاريخ الخروج',
        'hotelDetails.guests': 'الضيوف',
        'hotelDetails.checkAvailability': 'تحقق من التوفر',
        'hotelDetails.contactHotel': 'اتصل بالفندق',
        'hotelDetails.selectRoom': 'اختر الغرفة',
        'hotelDetails.viewDetails': 'عرض التفاصيل',
        'hotelDetails.wasHelpful': 'هل كان هذا مفيداً؟',
        'hotelDetails.amenityCategories': {
            'general': 'عام',
            'room': 'مرافق الغرفة',
            'dining': 'المطاعم',
            'wellness': 'الراحة والترفيه',
            'business': 'الأعمال'
        },
        'hotelDetails.highlight1': 'موقع مميز في السلطان أحمد التاريخي',
        'hotelDetails.highlight2': 'إطلالات خلابة على المسجد الأزرق وآيا صوفيا',
        'hotelDetails.highlight3': 'مركز راحة ورفاهية فاخر',
        'hotelDetails.highlight4': 'خيارات طعام متعددة مع المطبخ التركي',
        'hotelDetails.highlight5': 'خدمة استقبال على مدار الساعة',
        'hotelDetails.cancellation': 'الإلغاء',
        'hotelDetails.pets': 'الحيوانات الأليفة',
        'hotelDetails.smoking': 'التدخين',
        
        // Booking translations
        'booking.title': 'احجز إقامتك',
        'booking.newSearch': 'بحث جديد',
        'booking.selectDates': 'يرجى اختيار تواريخ الدخول والخروج',
        'booking.invalidDates': 'تاريخ الخروج يجب أن يكون بعد تاريخ الدخول',
        'booking.searchError': 'خطأ في البحث عن التوفر',
        'booking.guest': 'ضيف',
        'booking.guests': 'ضيوف',
        'booking.searching': 'جاري البحث...',
        'booking.selectRoom': 'اختر غرفتك',
        'booking.roomType1': 'غرفة ديلوكس',
        'booking.roomType2': 'جناح تنفيذي',
        'booking.roomType3': 'جناح رئاسي',
        'booking.roomDescription1': 'غرفة مريحة مع إطلالة على المدينة',
        'booking.roomDescription2': 'جناح واسع مع منطقة معيشة منفصلة',
        'booking.roomDescription3': 'رفاهية قصوى مع تراس خاص',
        'booking.roomsAvailable': 'غرف متاحة',
        'booking.soldOut': 'نفدت الغرف',
        'booking.bookingSummary': 'ملخص الحجز',
        'booking.dates': 'التواريخ',
        'booking.total': 'المجموع',
        'booking.continue': 'متابعة',
        'booking.guestDetails': 'تفاصيل الضيف',
        'booking.firstName': 'الاسم الأول',
        'booking.lastName': 'الاسم الأخير',
        'booking.email': 'البريد الإلكتروني',
        'booking.phone': 'الهاتف',
        'booking.specialRequests': 'طلبات خاصة',
        'booking.specialRequestsPlaceholder': 'أي طلبات أو تفضيلات خاصة...',
        'booking.fillRequiredFields': 'يرجى ملء جميع الحقول المطلوبة',
        'booking.bookingError': 'خطأ في إنشاء الحجز',
        'booking.processing': 'جاري المعالجة...',
        'booking.confirmBooking': 'تأكيد الحجز',
        'booking.bookingConfirmed': 'تم تأكيد الحجز!',
        'booking.confirmationMessage': 'تم تأكيد حجزك بنجاح. ستتلقى رسالة تأكيد بالبريد الإلكتروني قريباً.',
        'booking.confirmationNumber': 'رقم التأكيد',
        'booking.bookingId': 'رقم الحجز',
        'booking.totalPaid': 'المبلغ المدفوع',
        'booking.printConfirmation': 'طباعة التأكيد',
        'booking.newBooking': 'حجز جديد',
    },

    tr: {
        // Navigation
        'nav.home': 'Ana Sayfa',
        'nav.destinations': 'Destinasyonlar',
        'nav.hotels': 'Oteller',
        'nav.partnerHotels': 'Partner Oteller',
        'nav.flights': 'Uçuşlar',
        'nav.packages': 'Paketler',
        'nav.about': 'Hakkımızda',
        'nav.signIn': 'Giriş Yap',
        'nav.signUp': 'Kayıt Ol',
        'nav.bookNow': 'Şimdi Rezerve Et',
        'nav.myAccount': 'Hesabım',
        'nav.signOut': 'Çıkış Yap',
        'nav.hi': 'Merhaba',

        // Search Bar
        'search.city': 'Şehir',
        'search.hotel': 'Otel Adı',
        'search.checkIn': 'Giriş Tarihi',
        'search.checkOut': 'Çıkış Tarihi',
        'search.search': 'Ara',
        'search.cityPlaceholder': 'örn: İstanbul',
        'search.hotelPlaceholder': 'İsteğe bağlı',
        'search.errorCityOrHotel': 'Lütfen şehir adı veya otel adı girin',
        'search.errorDates': 'Lütfen giriş ve çıkış tarihlerini seçin',
        'search.errorDateOrder': 'Çıkış tarihi giriş tarihinden sonra olmalıdır',

        // Search Results
        'results.title': 'Arama Sonuçları',
        'results.description': 'Konaklamanız için en iyi otelleri bulun.',
        'results.showingIn': 'Şu şehirdeki oteller gösteriliyor:',
        'results.matching': 'eşleşen',
        'results.loading': 'Oteller yükleniyor...',
        'results.noResults': 'Otel bulunamadı.',

        // Home Page
        'home.heroTitle': 'Mükemmel Yolculuğunuzu Keşfedin',
        'home.heroSubtitle': 'MB Tourism ile dünyayı keşfedin - Güvenilir seyahat ortağınız',
        'home.features': 'Neden Bizi Seçmelisiniz',
        'home.featuresDesc': 'Premium hizmetlerimizle farkı yaşayın',
        'home.destinations': 'Popüler Destinasyonlar',
        'home.destinationsDesc': 'En çok aranan seyahat destinasyonlarını keşfedin',
        'home.offers': 'Özel Teklifler',
        'home.offersDesc': 'Kaçırmak istemeyeceğiniz sınırlı süreli fırsatlar',
        'home.cta': 'Yolculuğunuza Başlamaya Hazır mısınız?',
        'home.ctaDesc': 'Maceraları için MB Tourism\'e güvenen binlerce gezgine katılın',
        'home.getStarted': 'Başlayın',
        'home.contactUs': 'İletişime Geçin',

        // AI Chat
        'ai.askMe': 'Sor Bana',
        'ai.title': 'Akıllı Seyahat Asistanı',
        'ai.status': 'Şimdi Çevrimiçi',
        'ai.placeholder': 'Sorunuzu buraya yazın...',
        'ai.close': '✕',

        // Account
        'account.welcome': 'Tekrar hoş geldiniz',
        'account.description': 'Hesabınızı yönetin ve seyahat geçmişinizi görüntüleyin',
        'account.profileInfo': 'Profil Bilgileri',
        'account.editProfile': 'Profili Düzenle',
        'account.saveChanges': 'Değişiklikleri Kaydet',
        'account.cancelEdit': 'İptal',
        'account.profileUpdated': 'Profil başarıyla güncellendi!',
        'account.bookings': 'Rezervasyonlar',
        'account.destinations': 'Destinasyonlar',
        'account.memberSince': 'Üye Olma Tarihi',
        'account.status': 'Durum',
        'account.recentActivity': 'Son Aktivite',
        'account.accountSettings': 'Hesap Ayarları',
        'account.changePassword': 'Şifre Değiştir',
        'account.privacySettings': 'Gizlilik Ayarları',
        'account.notifications': 'Bildirim Tercihleri',
        'account.logout': 'Çıkış Yap',
        'account.fullName': 'Tam Ad',
        'account.email': 'E-posta',
        'account.userId': 'Kullanıcı ID',
        'account.enterFullName': 'Tam adınızı girin',
        'account.enterEmail': 'E-posta adresinizi girin',
        'account.saving': 'Kaydediliyor...',
        'account.confirmed': 'Onaylandı',
        'account.completed': 'Tamamlandı',
        'account.viewed': 'Görüntülendi',

        // Auth
        'auth.welcomeBack': 'Tekrar hoş geldiniz',
        'auth.signInToContinue': 'MB Tourism\'e devam etmek için giriş yapın',
        'auth.email': 'E-posta',
        'auth.emailPlaceholder': 'you@example.com',
        'auth.password': 'Şifre',
        'auth.passwordPlaceholder': '••••••••',
        'auth.signIn': 'Giriş Yap',
        'auth.signingIn': 'Giriş yapılıyor...',
        'auth.noAccount': 'Hesabınız yok mu?',
        'auth.createAccount': 'Hesap oluşturun',
        'auth.nameRequired': 'Ad gerekli',
        'auth.emailRequired': 'E-posta gerekli',
        'auth.passwordRequired': 'Şifre gerekli',
        'auth.invalidEmail': 'Geçersiz e-posta',
        'auth.passwordMinLength': 'Şifre en az 4 karakter olmalıdır',
        'auth.loginFailed': 'Giriş başarısız',
        'auth.fullName': 'Tam ad',
        'auth.namePlaceholder': 'Adınız',
        'auth.joinInMinute': 'MB Tourism\'e bir dakikada katılın',
        'auth.registrationFailed': 'Kayıt başarısız',
        'auth.creatingAccount': 'Hesap oluşturuluyor...',
        'auth.haveAccount': 'Zaten hesabınız var mı?',

        // Forgot Password
        'forgotPassword.title': 'Şifrenizi mi Unuttunuz?',
        'forgotPassword.subtitle': 'E-posta adresinizi girin, size şifre sıfırlama bağlantısı gönderelim.',
        'forgotPassword.email': 'E-posta Adresi',
        'forgotPassword.emailPlaceholder': 'E-posta adresinizi girin',
        'forgotPassword.sendResetLink': 'Sıfırlama Bağlantısı Gönder',
        'forgotPassword.sending': 'Gönderiliyor...',
        'forgotPassword.successMessage': 'Şifre sıfırlama bağlantısı e-posta adresinize gönderildi.',
        'forgotPassword.errorMessage': 'Lütfen geçerli bir e-posta adresi girin.',
        'forgotPassword.rememberPassword': 'Şifrenizi hatırladınız mı?',
        'forgotPassword.backToLogin': 'Girişe Geri Dön',
        'forgotPassword.noAccount': 'Hesabınız yok mu?',
        'forgotPassword.createAccount': 'Hesap Oluştur',

        // Reset Password
        'resetPassword.title': 'Şifre Sıfırla',
        'resetPassword.subtitle': 'Yeni şifrenizi aşağıya girin.',
        'resetPassword.newPassword': 'Yeni Şifre',
        'resetPassword.passwordPlaceholder': 'Yeni şifrenizi girin',
        'resetPassword.confirmPassword': 'Şifre Onayla',
        'resetPassword.confirmPasswordPlaceholder': 'Yeni şifrenizi onaylayın',
        'resetPassword.requirements': 'Şifre Gereksinimleri',
        'resetPassword.minLength': 'En az 6 karakter',
        'resetPassword.passwordsMatch': 'Şifreler eşleşiyor',
        'resetPassword.resetPassword': 'Şifre Sıfırla',
        'resetPassword.resetting': 'Sıfırlanıyor...',
        'resetPassword.successMessage': 'Şifre başarıyla sıfırlandı! Giriş sayfasına yönlendiriliyor...',
        'resetPassword.passwordsNotMatch': 'Şifreler eşleşmiyor.',
        'resetPassword.passwordTooShort': 'Şifre en az 6 karakter uzunluğunda olmalıdır.',
        'resetPassword.invalidToken': 'Geçersiz veya süresi dolmuş sıfırlama bağlantısı.',
        'resetPassword.invalidLink': 'Geçersiz Sıfırlama Bağlantısı',
        'resetPassword.invalidLinkMessage': 'Bu şifre sıfırlama bağlantısı geçersiz veya süresi dolmuş.',
        'resetPassword.requestNewLink': 'Yeni Bağlantı İste',
        'resetPassword.rememberPassword': 'Şifrenizi hatırladınız mı?',
        'resetPassword.backToLogin': 'Girişe Geri Dön',

        // Destinations
        'destinations.title': 'Destinasyonlar',
        'destinations.subtitle': 'Dünya çapında harika yerleri keşfedin',
        'destinations.featured': 'Öne Çıkan Destinasyonlar',
        'destinations.featuredDesc': 'Unutulmaz deneyimler için en iyi seçimlerimiz',
        'destinations.allDestinations': 'Tüm Destinasyonlar',
        'destinations.destinationsFound': 'destinasyon bulundu',
        'destinations.noDestinations': 'Kriterlerinize uygun destinasyon bulunamadı',
        'destinations.noResultsTitle': 'Destinasyon Bulunamadı',
        'destinations.noResultsDesc': 'Filtrelerinizi veya arama kriterlerinizi ayarlamayı deneyin',
        'destinations.explore': 'Keşfet',
        'destinations.search': 'Ara',
        'destinations.searchPlaceholder': 'Destinasyon, ülke...',
        'destinations.continent': 'Kıta',
        'destinations.allContinents': 'Tüm Kıtalar',
        'destinations.category': 'Kategori',
        'destinations.allCategories': 'Tüm Kategoriler',
        'destinations.filters': 'Filtreler',
        'destinations.clearFilters': 'Tümünü Temizle',
        'destinations.priceRange': 'Fiyat Aralığı',
        'destinations.minRating': 'Minimum Puan',
        'destinations.sortBy': 'Sırala',
        'destinations.sortName': 'İsim',
        'destinations.sortPrice': 'Fiyat',
        'destinations.sortRating': 'Puan',
        'destinations.bestTime': 'En İyi Zaman',
        'destinations.duration': 'Süre',
        'destinations.price': 'Fiyat',

        // Flights
        'flights.title': 'Uçuşlar',
        'flights.subtitle': 'Hedefinize en iyi uçuşları bulun',
        'flights.from': 'Nereden',
        'flights.fromPlaceholder': 'Şehir veya Havalimanı',
        'flights.to': 'Nereye',
        'flights.toPlaceholder': 'Şehir veya Havalimanı',
        'flights.departure': 'Gidiş Tarihi',
        'flights.return': 'Dönüş Tarihi (İsteğe bağlı)',
        'flights.passengers': 'Yolcu Sayısı',
        'flights.cabinClass': 'Kabin Sınıfı',
        'flights.search': 'Uçuş Ara',
        'flights.filters': 'Filtreler',
        'flights.clearFilters': 'Tümünü Temizle',
        'flights.priceRange': 'Fiyat Aralığı',
        'flights.maxStops': 'Maksimum Durak',
        'flights.direct': 'Direkt',
        'flights.oneStop': '1 Durak',
        'flights.twoStops': '2 Durak',
        'flights.sortBy': 'Sırala',
        'flights.sortPrice': 'Fiyat',
        'flights.sortDuration': 'Süre',
        'flights.sortDeparture': 'Kalkış Zamanı',
        'flights.results': 'Sonuçlar',
        'flights.flightsFound': 'uçuş bulundu',
        'flights.noFlights': 'Kriterlerinize uygun uçuş bulunamadı',
        'flights.noResultsTitle': 'Uçuş Bulunamadı',
        'flights.noResultsDesc': 'Arama kriterlerinizi ayarlamayı deneyin',
        'flights.bookNow': 'Şimdi Rezerve Et',
        'flights.stops': 'durak',

        // Hotels
        'hotels.title': 'Oteller',
        'hotels.subtitle': 'Yolculuğunuz için mükemmel konaklama yerini keşfedin',
        'hotels.filters': 'Filtreler',
        'hotels.clearFilters': 'Tümünü Temizle',
        'hotels.search': 'Otel Ara',
        'hotels.searchPlaceholder': 'Otel adı veya konum...',
        'hotels.city': 'Şehir',
        'hotels.allCities': 'Tüm Şehirler',
        'hotels.priceRange': 'Fiyat Aralığı',
        'hotels.minRating': 'Minimum Puan',
        'hotels.sortBy': 'Sırala',
        'hotels.sortName': 'İsim',
        'hotels.sortPrice': 'Fiyat',
        'hotels.sortRating': 'Puan',
        'hotels.results': 'Sonuçlar',
        'hotels.hotelsFound': 'otel bulundu',
        'hotels.noHotels': 'Kriterlerinize uygun otel bulunamadı',
        'hotels.noResultsTitle': 'Otel Bulunamadı',
        'hotels.noResultsDesc': 'Filtrelerinizi veya arama kriterlerinizi ayarlamayı deneyin',
        'hotels.bookNow': 'Şimdi Rezerve Et',

        // Packages
        'packages.title': 'Seyahat Paketleri',
        'packages.subtitle': 'Her bütçe ve tercihe uygun harika seyahat paketlerini keşfedin',
        'packages.featured': 'Öne Çıkan Paketler',
        'packages.featuredDesc': 'En popüler ve yüksek puanlı seyahat deneyimlerimiz',
        'packages.allPackages': 'Tüm Paketler',
        'packages.packagesFound': 'paket bulundu',
        'packages.noPackages': 'Kriterlerinize uygun paket bulunamadı',
        'packages.noResultsTitle': 'Paket Bulunamadı',
        'packages.noResultsDesc': 'Filtrelerinizi veya arama kriterlerinizi ayarlamayı deneyin',
        'packages.explore': 'Paketi Keşfet',
        'packages.search': 'Ara',
        'packages.searchPlaceholder': 'Paket, destinasyon...',
        'packages.category': 'Kategori',
        'packages.allCategories': 'Tüm Kategoriler',
        'packages.duration': 'Süre',
        'packages.allDurations': 'Tüm Süreler',
        'packages.priceRange': 'Fiyat Aralığı',
        'packages.minRating': 'Minimum Puan',
        'packages.sortBy': 'Sırala',
        'packages.sortName': 'İsim',
        'packages.sortPrice': 'Fiyat',
        'packages.sortRating': 'Puan',
        'packages.sortDuration': 'Süre',
        'packages.groupSize': 'Grup Büyüklüğü',
        'packages.difficulty': 'Zorluk',
        'packages.price': 'Fiyat',
        'packages.filters': 'Filtreler',
        'packages.clearFilters': 'Tümünü Temizle',

        // About Us
        'about.hero.title': 'MB Tourism Hakkında',
        'about.hero.subtitle': '2009\'dan beri unutulmaz seyahat deneyimleri yaratmada güvenilir ortağınız',
        'about.story.title': 'Hikayemiz',
        'about.story.paragraph1': 'MB Tourism, basit ama güçlü bir vizyonla kuruldu: dünya standartlarında seyahati herkes için erişilebilir kılmak. Küçük bir yerel acente olarak başlayan şirket, binlerce memnun müşteriye hizmet veren kapsamlı bir seyahat platformuna dönüştü.',
        'about.story.paragraph2': 'Yıllar boyunca, dünya çapında önde gelen oteller, havayolları ve tur operatörleriyle güçlü ortaklıklar kurduk, müşterilerimizin en iyi fiyatları ve olağanüstü hizmeti almasını sağladık.',
        'about.story.paragraph3': 'Mükemmellik ve müşteri memnuniyetine olan bağlılığımız, hayatın her alanından gezginlerin güvenini kazandı ve bizi turizm sektöründeki en güvenilir isimlerden biri haline getirdi.',
        'about.stats.clients': 'Mutlu Müşteri',
        'about.stats.destinations': 'Destinasyon',
        'about.stats.years': 'Yıl Deneyim',
        'about.stats.satisfaction': 'Memnuniyet Oranı',
        'about.values.title': 'Temel Değerlerimiz',
        'about.values.subtitle': 'Yaptığımız her şeyi yönlendiren ilkeler',
        'about.values.quality.title': 'Kaliteli Hizmet',
        'about.values.quality.desc': 'Hizmetlerimizin kalitesinden asla ödün vermeyiz, her seyahatin en yüksek standartları karşılamasını sağlarız.',
        'about.values.trust.title': 'Güven ve Güvenilirlik',
        'about.values.trust.desc': 'Şeffaflık, dürüstlük ve vaatlerin tutarlı bir şekilde yerine getirilmesi yoluyla kalıcı ilişkiler kurmak.',
        'about.values.innovation.title': 'İnovasyon',
        'about.values.innovation.desc': 'Platformumuzu ve hizmetlerimizi sürekli iyileştirerek en son teknoloji seyahat çözümleri sunmak.',
        'about.values.sustainability.title': 'Sürdürülebilirlik',
        'about.values.sustainability.desc': 'Yerel topluluklara fayda sağlayan ve gezegenimizi koruyan sorumlu turizmi teşvik etmek.',
        'about.team.title': 'Ekibimizle Tanışın',
        'about.team.subtitle': 'MB Tourism\'in başarısının arkasındaki tutkulu profesyoneller',
        'about.team.ceo': 'Genel Müdür',
        'about.team.ceo_desc': 'Turizm sektöründe 20 yılı aşkın deneyimle şirketimizi yönetiyor.',
        'about.team.cto': 'Teknoloji Müdürü',
        'about.team.cto_desc': 'Platformumuz genelinde inovasyon ve dijital dönüşümü yönlürüyor.',
        'about.team.cfo': 'Finans Müdürü',
        'about.team.cfo_desc': 'Sürdürülebilir başarı için finansal istikrar ve stratejik büyümeyi sağlıyor.',
        'about.team.cmo': 'Pazarlama Müdürü',
        'about.team.cmo_desc': 'Markamızı oluşturuyor ve dünya çapındaki gezginlerle bağlantı kuruyor.',
        'about.mission.title': 'Misyonumuz',
        'about.mission.description': 'İnsanları olağanüstü seyahat deneyimleri, yenilikçi teknoloji ve beklentileri aşan kişiselleştirilmiş hizmet sağlayarak dünyayı keşfetmeye ilham vermek ve bunu mümkün kılmak.',
        'about.cta.title': 'Yolculuğunuza Başlamaya Hazır mısınız?',
        'about.cta.description': 'Maceraları için MB Tourism\'e güvenen binlerce memnun gezgine katılın. Ömür boyu sürecek anılar yaratmanıza yardım edelim.',
        'about.cta.button': 'Şimdi Planlamaya Başlayın',

        // Partner Hotels
        'partnerHotels.title': 'Partner Oteller',
        'partnerHotels.subtitle': 'Dünya çapında lüks oteller ve resortların özel koleksiyonumuzu keşfedin',
        'partnerHotels.filters': 'Filtreler',
        'partnerHotels.clearFilters': 'Tümünü Temizle',
        'partnerHotels.search': 'Ara',
        'partnerHotels.searchPlaceholder': 'Otel adı veya konum...',
        'partnerHotels.location': 'Konum',
        'partnerHotels.allLocations': 'Tüm Konumlar',
        'partnerHotels.stars': 'Yıldız',
        'partnerHotels.category': 'Kategori',
        'partnerHotels.allCategories': 'Tüm Kategoriler',
        'partnerHotels.categories.luxury': 'Lüks',
        'partnerHotels.categories.business': 'İş',
        'partnerHotels.categories.resort': 'Resort',
        'partnerHotels.categories.boutique': 'Boutique',
        'partnerHotels.categories.apartment': 'Apartman',
        'partnerHotels.sortBy': 'Sırala',
        'partnerHotels.sortName': 'İsim',
        'partnerHotels.sortPrice': 'Fiyat',
        'partnerHotels.sortStars': 'Yıldız',
        'partnerHotels.results': 'Sonuçlar',
        'partnerHotels.hotelsFound': 'otel bulundu',
        'partnerHotels.noResultsTitle': 'Otel Bulunamadı',
        'partnerHotels.noResultsDesc': 'Filtrelerinizi veya arama kriterlerinizi ayarlamayı deneyin',
        'partnerHotels.exclusive': 'Özel',
        'partnerHotels.from': 'Başlangıç',
        'partnerHotels.discount': 'İndirim',
        'partnerHotels.viewDetails': 'Detayları Görüntüle',

        // Common
        'common.loading': 'Yükleniyor...',
        'common.error': 'Hata',
        'common.success': 'Başarılı',
        'common.save': 'Kaydet',
        'common.cancel': 'İptal',
        'common.edit': 'Düzenle',
        'common.delete': 'Sil',

        // Hotel Details
        'hotelDetails.loading': 'Otel detayları yükleniyor...',
        'hotelDetails.notFound': 'Otel bulunamadı',
        'hotelDetails.notFoundDesc': 'Aradığınız otel mevcut değil.',
        'hotelDetails.backToHotels': 'Otellere Geri Dön',
        'hotelDetails.overview': 'Genel Bakış',
        'hotelDetails.rooms': 'Odalar ve Fiyatlar',
        'hotelDetails.amenities': 'Olanaklar',
        'hotelDetails.reviews': 'Yorumlar',
        'hotelDetails.aboutHotel': 'Bu otel hakkında',
        'hotelDetails.whatMakesSpecial': 'Bu oteli özel kılan şeyler',
        'hotelDetails.hotelPolicies': 'Otel Politikaları',
        'hotelDetails.availableRooms': 'Mevcut Odalar',
        'hotelDetails.hotelAmenities': 'Otel Olanakları',
        'hotelDetails.guestReviews': 'Misafir Yorumları',
        'hotelDetails.startingFrom': 'Başlangıç fiyatı',
        'hotelDetails.perNight': 'gece başına',
        'hotelDetails.checkIn': 'Giriş',
        'hotelDetails.checkOut': 'Çıkış',
        'hotelDetails.guests': 'Misafirler',
        'hotelDetails.checkAvailability': 'Müsaitlik Kontrol Et',
        'hotelDetails.contactHotel': 'Otele İletişim',
        'hotelDetails.selectRoom': 'Oda Seç',
        'hotelDetails.viewDetails': 'Detayları Görüntüle',
        'hotelDetails.wasHelpful': 'Bu yararlı mıydı?',
        'hotelDetails.amenityCategories': {
            'general': 'Genel',
            'room': 'Oda Olanakları',
            'dining': 'Yemek',
            'wellness': 'Sağlık ve Rekreasyon',
            'business': 'İş'
        },
        'hotelDetails.highlight1': 'Tarihi Sultanahmet\'te özel konum',
        'hotelDetails.highlight2': 'Sultanahmet Camii ve Ayasofya\'ya muhteşem manzara',
        'hotelDetails.highlight3': 'Lüks spa ve wellness merkezi',
        'hotelDetails.highlight4': 'Türk mutfağı ile çoklu yemek seçenekleri',
        'hotelDetails.highlight5': '7/24 concierge hizmeti',
        'hotelDetails.cancellation': 'İptal',
        'hotelDetails.pets': 'Evcil Hayvanlar',
        'hotelDetails.smoking': 'Sigara',
        
        // Booking translations
        'booking.title': 'Rezervasyon Yapın',
        'booking.newSearch': 'Yeni Arama',
        'booking.selectDates': 'Lütfen giriş ve çıkış tarihlerini seçin',
        'booking.invalidDates': 'Çıkış tarihi giriş tarihinden sonra olmalıdır',
        'booking.searchError': 'Müsaitlik arama hatası',
        'booking.guest': 'Misafir',
        'booking.guests': 'Misafirler',
        'booking.searching': 'Aranıyor...',
        'booking.selectRoom': 'Odanızı Seçin',
        'booking.roomType1': 'Deluxe Oda',
        'booking.roomType2': 'Executive Suite',
        'booking.roomType3': 'Presidential Suite',
        'booking.roomDescription1': 'Şehir manzaralı rahat oda',
        'booking.roomDescription2': 'Ayrı oturma alanı olan geniş süit',
        'booking.roomDescription3': 'Özel teras ile en üst düzey lüks',
        'booking.roomsAvailable': 'oda müsait',
        'booking.soldOut': 'Tükendi',
        'booking.bookingSummary': 'Rezervasyon Özeti',
        'booking.dates': 'Tarihler',
        'booking.total': 'Toplam',
        'booking.continue': 'Devam Et',
        'booking.guestDetails': 'Misafir Detayları',
        'booking.firstName': 'Ad',
        'booking.lastName': 'Soyad',
        'booking.email': 'E-posta',
        'booking.phone': 'Telefon',
        'booking.specialRequests': 'Özel İstekler',
        'booking.specialRequestsPlaceholder': 'Herhangi bir özel istek veya tercih...',
        'booking.fillRequiredFields': 'Lütfen tüm gerekli alanları doldurun',
        'booking.bookingError': 'Rezervasyon oluşturma hatası',
        'booking.processing': 'İşleniyor...',
        'booking.confirmBooking': 'Rezervasyonu Onayla',
        'booking.bookingConfirmed': 'Rezervasyon Onaylandı!',
        'booking.confirmationMessage': 'Rezervasyonunuz başarıyla onaylandı. Yakında onay e-postası alacaksınız.',
        'booking.confirmationNumber': 'Onay Numarası',
        'booking.bookingId': 'Rezervasyon ID',
        'booking.totalPaid': 'Toplam Ödenen',
        'booking.printConfirmation': 'Onayı Yazdır',
        'booking.newBooking': 'Yeni Rezervasyon',
    },
};

export class LanguageService {
    private static instance: LanguageService;
    private currentLanguage: string = DEFAULT_LANGUAGE;
    private listeners: Array<(language: string) => void> = [];

    static getInstance(): LanguageService {
        if (!LanguageService.instance) {
            LanguageService.instance = new LanguageService();
        }
        return LanguageService.instance;
    }

    constructor() {
        // Always start with English as default, ignore browser language
        const saved = localStorage.getItem('selectedLanguage');
        if (saved && LANGUAGES.find(l => l.code === saved)) {
            this.currentLanguage = saved;
        } else {
            // Force English as default, don't auto-detect browser language
            this.currentLanguage = DEFAULT_LANGUAGE;
            localStorage.setItem('selectedLanguage', DEFAULT_LANGUAGE);
        }
        
        this.updateDocumentDirection();
    }

    getCurrentLanguage(): string {
        return this.currentLanguage;
    }

    setLanguage(code: string): void {
        const language = LANGUAGES.find(l => l.code === code);
        if (language) {
            this.currentLanguage = code;
            localStorage.setItem('selectedLanguage', code);
            this.updateDocumentDirection();
            this.notifyListeners();
        } else {
            console.warn(`Language code '${code}' is not supported. Available languages:`, LANGUAGES.map(l => l.code));
        }
    }

    getLanguageInfo(code?: string): Language {
        const langCode = code || this.currentLanguage;
        return LANGUAGES.find(l => l.code === langCode) || LANGUAGES[0];
    }

    translate(key: string, fallback?: string): string {
        const translations = TRANSLATIONS[this.currentLanguage as keyof typeof TRANSLATIONS];
        if (!translations) return fallback || key;
        
        // Handle nested keys like "hotelDetails.amenityCategories.general"
        const keys = key.split('.');
        let result: any = translations;
        
        for (const k of keys) {
            if (result && typeof result === 'object' && k in result) {
                result = result[k];
            } else {
                return fallback || key;
            }
        }
        
        return typeof result === 'string' ? result : (fallback || key);
    }

    t = this.translate; // Short alias

    subscribe(listener: (language: string) => void): () => void {
        this.listeners.push(listener);
        return () => {
            const index = this.listeners.indexOf(listener);
            if (index > -1) {
                this.listeners.splice(index, 1);
            }
        };
    }

    private updateDocumentDirection(): void {
        const langInfo = this.getLanguageInfo();
        document.documentElement.dir = langInfo.dir;
        document.documentElement.lang = langInfo.code;
    }

    private notifyListeners(): void {
        this.listeners.forEach(listener => listener(this.currentLanguage));
    }

    // Method to reset language to default
    resetToDefault(): void {
        this.setLanguage(DEFAULT_LANGUAGE);
    }

    // Method to clear stored language preference
    clearStoredLanguage(): void {
        localStorage.removeItem('selectedLanguage');
        this.resetToDefault();
    }

    // Method to force refresh the language state
    forceRefresh(): void {
        this.updateDocumentDirection();
        this.notifyListeners();
    }

    // Method to force set language and clear any cached state
    forceSetLanguage(code: string): void {
        const language = LANGUAGES.find(l => l.code === code);
        if (language) {
            this.currentLanguage = code;
            localStorage.setItem('selectedLanguage', code);
            this.updateDocumentDirection();
            this.notifyListeners();
        }
    }
}

export const languageService = LanguageService.getInstance();
